import { useEffect, useState } from 'react';
import { TextKey } from '@mews-commander/enums';
import { lt, showSuccessAlert } from '@mews/framework';
import { getWallClockDateUtcString } from '@mews-commander/utils';
import { useCreateVoucher } from '../../../../queries';
import { FormErrors } from '../../../../types';
import { VoucherContainer } from './VoucherContainer';
import { FormValues } from './types';

interface GroupedRates {
    enterpriseId: string;
    rateKeys?: string[] | null;
}

interface CreateVoucherFromRatesContainerProps {
    isOpened: boolean;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
    groupedRateKeys: GroupedRates[];
}

export const CreateVoucherFromRatesContainer = ({
    isOpened,
    onConfirm,
    onCancel,
    groupedRateKeys,
}: CreateVoucherFromRatesContainerProps) => {
    const [formErrors, setFormErrors] = useState<FormErrors>();

    const createVoucher = useCreateVoucher(true);

    const enterpriseIds = groupedRateKeys.map(({ enterpriseId }) => enterpriseId);

    const initialFormValues: FormValues = {
        name: '',
        key: '',
        occupiableIntervalEndUtc: '',
        occupiableIntervalStartUtc: '',
        companyKey: '',
        properties: enterpriseIds,
        rateKeys: [],
    };

    useEffect(() => {
        if (!isOpened) {
            setFormErrors(undefined);
        }
    }, [isOpened]);

    const handleOnSubmit = async ({
        properties,
        occupiableIntervalEndUtc,
        occupiableIntervalStartUtc,
        rateKeys,
        ...rest
    }: FormValues) => {
        await Promise.all(
            groupedRateKeys.map(async ({ enterpriseId, rateKeys: groupedRates }) =>
                createVoucher.mutateAsync(
                    {
                        occupiableIntervalStart: getWallClockDateUtcString(occupiableIntervalStartUtc) || null,
                        occupiableIntervalEnd: getWallClockDateUtcString(occupiableIntervalEndUtc) || null,
                        enterpriseIds: [enterpriseId],
                        rateKeys: groupedRates || [],
                        ...rest,
                    },
                    {
                        onSuccess: onConfirm,
                        onError: (e) => {
                            if (e.message.includes('CompanyKey')) {
                                setFormErrors({ field: 'companyKey', message: e.message });
                            }

                            if (e.message.includes('voucher key')) {
                                setFormErrors({ field: 'key', message: e.message });
                            }
                        },
                    }
                )
            )
        );

        showSuccessAlert({
            title: lt('VoucherCreated'),
        });
    };

    return (
        <VoucherContainer
            values={initialFormValues}
            active={isOpened}
            onCancel={onCancel}
            title={lt('CreateVoucher')}
            enterpriseIds={enterpriseIds}
            formErrors={formErrors}
            submitButtonTextKey={TextKey.Create}
            propertyCount={enterpriseIds.length}
            onSubmit={handleOnSubmit}
            isLoading={createVoucher.isPending}
            isCreatingFromRates
        />
    );
};
